import { handleError, logWarn, logInfo, clearErrorOnElement } from './errors.js';

export const calculateReadTime = (text) => Math.ceil(text.split(/\s+/).length / 200);

export const processReadTimeLink = async (element) => {
    let url = element.getAttribute("href");
    let articleId = element.getAttribute("vdx-readtime-link");
    let cacheKey = `${url}-${articleId}`;
    let cachedTime = localStorage.getItem(cacheKey);

    const findTargetElement = (el) => el.querySelector("[vdx-readtime-time]") || el.closest(".w-dyn-item")?.querySelector("[vdx-readtime-time]");
    const isValidTarget = (el, target) => {
        let parent = el.closest(".w-dyn-item");
        return parent ? parent.contains(target) : true;
    };

    if (cachedTime) {
        let targetElement = findTargetElement(element);
        if (targetElement && isValidTarget(element, targetElement)) {
            clearErrorOnElement(element);
            element.style.border = '';
            targetElement.textContent = `${cachedTime}`;
        } else {
            handleError(`vdx-readtime-link: no [vdx-readtime-time] target found in same .w-dyn-item. Add [vdx-readtime-time="${articleId}"] in the same list item.`, { module: 'readtime', element, showOnElement: true });
        }
        return;
    }

    try {
        let response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
        let html = await response.text();
        let doc = new DOMParser().parseFromString(html, "text/html");
        let articles = doc.querySelectorAll(`[vdx-readtime-article="${articleId}"]`);
        
        if (articles.length > 0) {
            let content = Array.from(articles).map(el => el.textContent.trim()).join(" ");
            if (content) {
                let readTime = calculateReadTime(content);
                let targetElement = findTargetElement(element);
                if (targetElement && isValidTarget(element, targetElement)) {
                    clearErrorOnElement(element);
                    element.style.border = '';
                    targetElement.textContent = `${readTime}`;
                    localStorage.setItem(cacheKey, readTime);
                } else {
                    handleError(`vdx-readtime-link: no [vdx-readtime-time] target in same .w-dyn-item. Add [vdx-readtime-time="${articleId}"].`, { module: 'readtime', element, showOnElement: true });
                }
            } else {
                handleError(`vdx-readtime-article="${articleId}" found on ${url} but has no text content. Add content to the article element.`, { module: 'readtime', element, showOnElement: true });
            }
        } else {
            handleError(`No [vdx-readtime-article="${articleId}"] found on ${url}. Check vdx-readtime-link value matches an article id on the linked page.`, { module: 'readtime', element, showOnElement: true });
        }
    } catch (error) {
        handleError(`vdx-readtime-link: fetch failed for ${url}. Check URL and network.`, { module: 'readtime', element, showOnElement: true, error });
    }
};

export const handleReadTimeOnPageLoad = () => {
    let articlesSet = new Set(Array.from(document.querySelectorAll("[vdx-readtime-article]")).map(el => el.getAttribute("vdx-readtime-article")));
    let timesSet = new Set(Array.from(document.querySelectorAll("[vdx-readtime-time]")).map(el => el.getAttribute("vdx-readtime-time")));

    articlesSet.forEach(articleId => {
        let articleElements = document.querySelectorAll(`[vdx-readtime-article="${articleId}"]`);
        let timeElements = Array.from(document.querySelectorAll(`[vdx-readtime-time="${articleId}"]`)).filter(el => !el.closest(".w-dyn-item"));

        if (timeElements.length > 0) {
            if (articleElements.length === 0) {
                timeElements.forEach((el) => handleError(`vdx-readtime-time="${articleId}" has no matching [vdx-readtime-article="${articleId}"]. Add an article element with that id.`, { module: 'readtime', element: el, showOnElement: true }));
            } else {
                let content = Array.from(articleElements).map(el => el.textContent.trim()).join(" ");
                if (content) {
                    let readTime = calculateReadTime(content);
                    timeElements.forEach(el => {
                        el.textContent = `${readTime}`;
                    });
                } else {
                    articleElements.forEach((el) => handleError(`vdx-readtime-article="${articleId}" has no text content. Add content to the article element.`, { module: 'readtime', element: el, showOnElement: true }));
                }
            }
        }
    });

    timesSet.forEach(timeId => {
        if (!articlesSet.has(timeId)) {
            Array.from(document.querySelectorAll(`[vdx-readtime-time="${timeId}"]`)).filter((el) => !el.closest('.w-dyn-item')).forEach((el) => {
                handleError(`vdx-readtime-time="${timeId}" has no matching [vdx-readtime-article="${timeId}"]. Add an article element with that id.`, { module: 'readtime', element: el, showOnElement: true });
            });
        }
    });

    window.fsAttributes = window.fsAttributes || [];
    window.fsAttributes.push(["cmsload", (listInstances) => {
        logInfo('Finsweet CMS Load detected!', { module: 'readtime' });

        if (!listInstances.length) {
            logWarn('No CMS list instances found', { module: 'readtime' });
            return;
        }

        const [listInstance] = listInstances;

        // Run whenever new items are rendered (pagination)
        listInstance.on("renderitems", (renderedItems) => {
            
            setTimeout(() => {
                
                document.querySelectorAll("[vdx-readtime-link]").forEach(processReadTimeLink);
            }, 0);
        });
    }]);
};
